import React, { useState } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Link as MuiLink
} from '@mui/material';
import { Link } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { 
  Warning,
  Info,
  SwapHoriz,
  CalendarToday,
} from '@mui/icons-material';
import { learnerService } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import ConfirmDialog from '../common/ConfirmDialog';
import { useContentPlayer } from '../../context/ContentPlayerContext';
import { learningPathService } from '../../services/api';
import { useEmbedded } from '../../hooks/useEmbedded';
import { tokens } from '../../theme';
const EnrollmentButton = styled(Button)(({ theme }) => ({
  width: '100%',
  marginBottom: theme.spacing(0.5)
}));

const WarningText = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
  marginTop: theme.spacing(0.5),
  marginBottom: theme.spacing(0.5)
}));

const LearningPathEnrollment = ({ 
  lpath, 
  instance, 
  enrolled,
  enrolledObj,
  progress,
  prerequisites,
  showEnrollCount,
  enrollmentCount,
  firstIncompleteContent,
  refreshOnComplete
}) => {
  const [loading, setLoading] = useState(false);
  const [flexibleEnrollmentOpen, setFlexibleEnrollmentOpen] = useState(false);
  const [selectedInstances, setSelectedInstances] = useState({});
  const { showToast } = useToast();
  const { openContentPlayer } = useContentPlayer();
  const isEmbedded = useEmbedded();
  
  // State for confirm dialogs
  const [unenrollDialogOpen, setUnenrollDialogOpen] = useState(false);
  // const [switchInstanceDialogOpen, setSwitchInstanceDialogOpen] = useState(false);
  const [switchingInstance, setSwitchingInstance] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, instanceId: null });
  // Check if all required prerequisites are completed
  const prerequisitesMet = !prerequisites?.prerequisites || 
    prerequisites?.prerequisites.length === 0 || 
    prerequisites?.required <= prerequisites?.completed;
  
  // Check if enrollment deadline has passed
  //const enrollmentDeadline = lpath?.enrollemnt_deadline || instance?.enrollment_deadline;
  const enrollmentDeadline = lpath?.lpath_end_date;

  const enrollDeadlinePassed = enrollmentDeadline && new Date(enrollmentDeadline) <= new Date();

  const startLearningPath = async () => {
    if (!prerequisitesMet) {
      showToast('warning', 'Please complete all required prerequisites first');
      return;
    }

    try {
      setLoading(true);
      const response = await learnerService.startLearningPath(
        lpath.id, 
        enrolledObj.id,
        firstIncompleteContent ? firstIncompleteContent.id : null
      );
      
      if (response.data.success) {
        showToast('Learning path started successfully','success');
        if (response.data.firstContentId !== null) {         
          openContentPlayer(
            lpath.id, 
            response.data.firstContentId, 
            `/learner/lpath/${lpath.id}`, 
            refreshOnComplete,
            true, // isLearnerMode
            response.data.clrId, // content learner reference ID if available
            lpath.id
          );
        }
        else {
          showToast('No content available for this learning path','error');
          if (refreshOnComplete) {
            refreshOnComplete();
          }
        }
      } else {
        showToast(response.data.error || 'Failed to start learning path','error');
      }
    } catch (error) {
      console.error('Error starting learning path:', error);
      showToast('Failed to start learning path','error');
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async (lpathId, instanceId) => {
    try {
      setLoading(true);
      const response = await learnerService.enrollInLearningPath(
        lpathId, 
        instanceId,
        Object.values(selectedInstances)
      );
      
      if (response.data.success) {
        console.log("Enrollment Response:", response.data);
       showToast('Successfully enrolled in the learning path', 'success'); 
        if (refreshOnComplete) {
          refreshOnComplete();
        } else {
          window.location.reload();
        }
      } else {
        showToast(response.data.message || 'Failed to enroll in learning path','error');
      }
    } catch (error) {
      console.error('Error enrolling in learning path:', error);
      showToast(error.response?.data?.message || 'Failed to enroll in learning path','error');
    } finally {
      setLoading(false);
      setFlexibleEnrollmentOpen(false);
    }
  };

  const handleUnenroll = async (lpathId) => {
    try {
      setLoading(true);
      const response = await learnerService.selfunenrollFromLearningPath(lpathId);
      
      if (response.data.success) {
        showToast('Successfully unenrolled from the learning path','success');
        window.location.reload();
      } else {
        showToast(response.data.message || 'Failed to unenroll from learning path','error');
      }
    } catch (error) {
      console.error('Error unenrolling from learning path:', error);
      showToast('Failed to unenroll from learning path','error');
    } finally {
      setLoading(false);
      setUnenrollDialogOpen(false);
    }
  };

  const openFlexibleEnrollment = () => {
    setFlexibleEnrollmentOpen(true);
  };

  const handleInstanceChange = (courseId, instanceId) => {
    setSelectedInstances(prev => ({
      ...prev,
      [courseId]: instanceId
    }));
  };

  // Function to handle learning path instance switch (moved from LearningPathDetails)
  const handleSwitchInstance = (instanceId) => {
    setConfirmDialog({
      open: true,
      instanceId
    });
  };

  // Function to confirm instance switch (moved from LearningPathDetails)
  const confirmSwitchInstance = async () => {
    const instanceId = confirmDialog.instanceId;
    if (!instanceId) return;
    
    setConfirmDialog({ open: false, instanceId: null });
    setSwitchingInstance(true);
    
    try {
      const response = await learningPathService.switchInstance(lpath.id, instanceId);
      if (response.data.success) {
        showToast('Successfully switched to the new instance','success');
        // Navigate to the learning path with the new instance
        window.location.reload();
      } else {
        showToast(response.data.message || 'Failed to switch instance','error');
      }
    } catch (error) {
      console.error('Error switching instance:', error);
      showToast(error.response?.data?.message || 'Failed to switch instance','error');
    } finally {
      setSwitchingInstance(false);
    }
  };

  // Check if user is enrolled in a different instance
  //const isEnrolledInDifferentInstance = enrolledObj && enrolledObj.lpath_instance_id && enrolledObj.lpath_instance_id !== instance?.id;
  const isEnrolledInDifferentInstance = false;
  // Determine button text based on progress
  const getButtonText = () => {
    if (!progress) return 'Start';
    if (progress.progress === 0) return 'Start';
    if (progress.progress >= 100) return 'Revisit';
    return 'Continue';
  };

  //const canSelfUnenroll = [true, 1, '1', 'true'].includes(lpath.self_unenrollment);
  const canSelfUnenroll = !lpath.lpath_end_date ? true : false;

  const formatDueDate = (dateString) => (
    dateString ? new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }) : ''
  );

  const getDaysRemaining = (dateString) => {
    if (!dateString) return null;

    const today = new Date();
    const dueDate = new Date(dateString);
    const difference = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));

    return difference >= 0 ? `${difference} days left` : 'Overdue';
  };

  // Render enrollment count for admin/manager roles
  if (showEnrollCount) {
    return (
      <Box sx={{ mb: 1.5 }}>
        <EnrollmentButton variant="outlined" disabled>
          {enrollmentCount} Enrollment(s)
        </EnrollmentButton>
      </Box>
    );
  }

  // Render start/continue button for enrolled users
  //if (enrolled && instance?.id === enrolledObj?.lpath_instance_id) {
  if (enrolled) {
    const buttonText = getButtonText();
    const disableStart = !prerequisitesMet;

    return (
      <Box sx={{
        mb: 1.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }} >
        <Box>
          <Tooltip title={disableStart ? "Complete required prerequisites first" : ""}>
            <span style={{ width: '100%' }}>
              <EnrollmentButton 
                variant="contained" 
                sx={{ backgroundColor: (progress?.progress >= 100) ? tokens.color.brand : '', color: (progress?.progress >= 100) ? tokens.color.textPrimary : '', '&:hover':  {backgroundColor: (progress?.progress >= 100) ? tokens.color.brandLight : ''}}}
                //color="primary"
                disabled={disableStart || loading}
                onClick={startLearningPath}
              >
                {loading ? <CircularProgress size={24} /> : buttonText}
              </EnrollmentButton>
            </span>
          </Tooltip> 
        </Box>  
        
        {lpath?.lpath_end_date && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              minHeight: 38,
              mb: 2,
              px: 1.5,
              border: `1px solid ${tokens.color.borderSecondary}`,
              borderRadius: tokens.radius.md,
              backgroundColor: tokens.color.bgSecondary,
              color: tokens.color.textSecondary,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, color: tokens.color.textPrimary }}>
              Due {formatDueDate(lpath?.lpath_end_date)}
            </Typography>

            <Typography variant="body2" sx={{ color: tokens.color.textTertiary }}>
              · {getDaysRemaining(lpath?.lpath_end_date)}
            </Typography>
          </Box>
        )}

        {disableStart && (
          <Box>
            <WarningText sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Warning fontSize="small" color="warning" /> 
              Complete all required prerequisites!
            </WarningText>
          </Box>
        )}

        {canSelfUnenroll && (
          <MuiLink 
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              minHeight: 20,
              mb: 2,
              px: 1.5,
              cursor:'pointer',
            }}                                         
            onClick={() => setUnenrollDialogOpen(true)}
          >
              Unenroll from this Learning Path            
          </MuiLink>
        )}

        {/* Unenroll Confirmation Dialog */}
        <ConfirmDialog
          open={unenrollDialogOpen}
          title="Confirm Unenrollment"
          content="Are you sure you want to unenroll from this learning path? This action cannot be undone."
          onConfirm={() => handleUnenroll(lpath.id)}
          onCancel={() => setUnenrollDialogOpen(false)}
        />
      </Box>
    );
  }

  // Add Switch Instance button if user is enrolled in a different instance
  if (isEnrolledInDifferentInstance) {
    return (
      <Box sx={{ mb: 1.5 }}>
        <EnrollmentButton
          startIcon={<SwapHoriz />}
          variant="contained"
          color="primary"
          disabled={switchingInstance}
          onClick={() => handleSwitchInstance(instance.id)}
        >
          {switchingInstance ? <CircularProgress size={24} /> : 'Switch to This Instance'}
        </EnrollmentButton>
        
        <WarningText sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Info fontSize="small" color="info" /> 
          <span>
            You are currently enrolled in a different instance of this learning path.{' '}
            <Link 
              to={`${isEmbedded ? '/embed' : ''}/learner/lpath/${lpath.id}/${enrolledObj.lpath_instance_id}`}
              style={{ textDecoration: 'underline', cursor: 'pointer', color: 'inherit' }}
            >
              Go to enrolled instance
            </Link>
          </span>
        </WarningText>
        
        {/* Confirmation Dialog for switching instance */}
        <Dialog
          open={confirmDialog.open}
          onClose={() => setConfirmDialog({ open: false, instanceId: null })}
        >
          <DialogTitle>Switch Instance</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              Are you sure you want to switch to a different instance? Your progress will be transferred to the new instance.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDialog({ open: false, instanceId: null })}>
              Cancel
            </Button>
            <Button onClick={confirmSwitchInstance} color="primary" autoFocus>
              Switch
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // Render enrollment options for non-enrolled users
  if (enrollDeadlinePassed) {
    return (
      <Box sx={{ mb: 1.5 }}>
        <EnrollmentButton variant="outlined" disabled>
          Enrollment Closed
        </EnrollmentButton>
        <WarningText sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          Enrollment deadline has passed.
        </WarningText>
      </Box>
    );
  }

  if (!prerequisitesMet) {
    return (
      <Box sx={{ mb: 1.5 }}>
        <Tooltip title="Complete required prerequisites first">
          <span style={{ width: '100%' }}>
            <EnrollmentButton variant="outlined" disabled>
              Enroll
            </EnrollmentButton>
          </span>
        </Tooltip>
        <WarningText sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Warning fontSize="small" color="warning" /> 
          Please complete all required prerequisites!
        </WarningText>
      </Box>
    );
  }

  // Handle flexible enrollment
  
  if (instance?.is_flexible === '1') {
    
    return (
      <Box sx={{ mb: 1.5 }}>
        <EnrollmentButton 
          variant="contained" 
          color="primary"
          disabled={loading}
          onClick={openFlexibleEnrollment}
        >
          {loading ? <CircularProgress size={24} /> : 'Enroll'}
        </EnrollmentButton>
        
        <WarningText sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Info fontSize="small" color="info" /> 
          This learning path allows you to select course instances
        </WarningText>

        {/* Flexible Enrollment Dialog */}
        <Dialog 
          open={flexibleEnrollmentOpen} 
          onClose={() => setFlexibleEnrollmentOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Select Course Instances</DialogTitle>
          <DialogContent>
            <Typography variant="body2" paragraph>
              This learning path contains courses with multiple instances. 
              Please select which instance you'd like to enroll in for each course.
            </Typography>
            
            {/* Course instance selection */}
            {lpath.courses && lpath.courses.map(course => (
              <Box key={course.id} sx={{ mb: 2 }}>
                <Typography variant="subtitle1">{course.title}</Typography>
                <FormControl fullWidth sx={{ mt: 1 }}>
                  <InputLabel id={`instance-select-${course.id}`}>Select Instance</InputLabel>
                  <Select
                    labelId={`instance-select-${course.id}`}
                    value={selectedInstances[course.id] || ''}
                    onChange={(e) => handleInstanceChange(course.id, e.target.value)}
                    label="Select Instance"
                    >
                    {course.instances && course.instances.map(inst => (
                        <MenuItem key={inst.id} value={inst.id}>
                        {inst.title || `Instance ${inst.id}`}
                        {inst.default_instance === '1' && ' (Default)'}
                        </MenuItem>
                    ))}
                    </Select>
                </FormControl>
                </Box>
            ))}
            </DialogContent>
            <DialogActions>
            <Button onClick={() => setFlexibleEnrollmentOpen(false)}>Cancel</Button>
            <Button 
                onClick={() => handleEnroll(lpath.id, instance.id)} 
                variant="contained" 
                color="primary"
                disabled={loading}
            >
                {loading ? <CircularProgress size={24} /> : 'Enroll'}
            </Button>
            </DialogActions>
        </Dialog>
        </Box>
    );
    }

    // Default: regular enrollment
    return (
    <Box sx={{ mb: 1.5 }}>
        <EnrollmentButton 
        variant="contained" 
        color="primary"
        disabled={loading}
        onClick={() => handleEnroll(lpath.id, instance.id)}
        >
        {loading ? <CircularProgress size={24} /> : 'Enroll'}
        </EnrollmentButton>
    </Box>
    );
};

export default LearningPathEnrollment;
